import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useUploadAnalysisSnapshot } from '@/features/website-analysis/api/upload-snapshot';
import type { WebsiteAnalysisSnapshot } from '@borradh-workspace/api-client/types';
import { UploadIcon } from 'lucide-react';
import { useRef } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';

interface Step3WebsiteAnalysisProps {
  // biome-ignore lint/suspicious/noExplicitAny: Form type flexibility needed
  form: UseFormReturn<any>;
  isAnalyzing?: boolean;
  /**
   * Called when the operator uploads a validated analysis snapshot.
   *
   * Onboarding is now operator-driven: rather than rely on the one-size-fits-all
   * scanner (which either times out or misses information on sites with unusual
   * DOMs), an operator runs Claude Code against the target site off-app,
   * uploads the resulting JSON here, and it flows through the SAME field
   * mapping the scanner uses (`apply-snapshot-to-form.ts`).
   *
   * The scanner is retained as a fallback — the URL fields still work, and
   * pressing Continue with a URL and no upload triggers the automated scan.
   */
  onSnapshotUploaded?: (result: WebsiteAnalysisSnapshot) => void;
}

/**
 * Step 3: Website Scanner
 * URL inputs for the automated scan + an operator upload path for a JSON
 * snapshot produced off-app with Claude Code. Analysis is triggered by the
 * Continue button (via onBeforeContinue); upload flows through onSnapshotUploaded.
 */
export function Step3WebsiteAnalysis({
  form,
  isAnalyzing,
  onSnapshotUploaded,
}: Step3WebsiteAnalysisProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { uploadSnapshotAsync, isUploading } = useUploadAnalysisSnapshot({
    onSuccess: (result) => onSnapshotUploaded?.(result),
  });

  const disabled = isAnalyzing || isUploading;

  return (
    <FieldGroup className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Enter your website</h1>
        <p className="text-muted-foreground">
          We&apos;ll analyze your website and booking system to auto-fill your
          business details. You can skip this step if you don&apos;t have
          either.
        </p>
      </div>

      {/* Website URL */}
      <Controller
        name="websiteUrl"
        control={form.control}
        render={({ field, fieldState }) => (
          <Field
            className="flex flex-col gap-2"
            data-invalid={fieldState.invalid}
          >
            <FieldLabel
              className="text-secondary-foreground text-sm font-medium"
              htmlFor={field.name}
              aria-invalid={fieldState.invalid}
            >
              Website URL (optional)
            </FieldLabel>
            <Input
              {...field}
              id={field.name}
              type="url"
              placeholder="https://www.yourbusiness.com"
              aria-invalid={fieldState.invalid}
              autoComplete="url"
              disabled={disabled}
            />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />

      {/* Booking System URL */}
      <Controller
        name="bookingSystemUrl"
        control={form.control}
        render={({ field, fieldState }) => (
          <Field
            className="flex flex-col gap-2"
            data-invalid={fieldState.invalid}
          >
            <FieldLabel
              className="text-secondary-foreground text-sm font-medium"
              htmlFor={field.name}
              aria-invalid={fieldState.invalid}
            >
              Booking System Link (optional)
            </FieldLabel>
            <p className="text-muted-foreground text-xs">
              If you use an online booking system (e.g. Fresha, Treatwell,
              Phorest), paste the link here and we&apos;ll scan it for your
              services and prices.
            </p>
            <Input
              {...field}
              id={field.name}
              type="url"
              placeholder="https://www.fresha.com/book-now/your-salon"
              aria-invalid={fieldState.invalid}
              autoComplete="url"
              disabled={disabled}
            />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />

      {/* Loading State (scanner) */}
      {isAnalyzing && (
        <div className="space-y-3 rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">
            Analyzing your website and booking system...
          </p>
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      )}

      {/* Operator upload path — the SCV/JSON that replaces the scanner */}
      <div className="border-t pt-5">
        <div className="flex flex-col gap-2">
          <FieldLabel className="text-secondary-foreground text-sm font-medium">
            Operator: upload an analysis snapshot (JSON)
          </FieldLabel>
          <p className="text-muted-foreground text-xs">
            If you produced a website-analysis snapshot off-app with Claude
            Code, upload the JSON here to populate the wizard directly. This
            skips the automated scanner and is the recommended path for sites
            with unusual layouts. See{' '}
            <code className="text-xs">
              docs/operators/website-analysis-snapshot.md
            </code>{' '}
            for the format and the operator brief.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              // Reset the input so the same filename can be re-uploaded after
              // a validation failure.
              e.target.value = '';
              try {
                await uploadSnapshotAsync(file);
              } catch {
                // toast is emitted by the hook
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            className="self-start"
          >
            <UploadIcon className="mr-2 h-4 w-4" />
            {isUploading ? 'Loading snapshot…' : 'Upload analysis JSON'}
          </Button>
        </div>
      </div>
    </FieldGroup>
  );
}
